import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import SearchBar from "@/components/site/SearchBar";
import YachtCard from "@/components/site/YachtCard";
import FleetFilters, { applyFilters, DEFAULT_FILTERS, type Filters } from "@/components/site/FleetFilters";
import { useLang } from "@/lib/i18n";
import { api, fmtDate } from "@/lib/api";
import type { Yacht } from "@/types";

export default function Yachts() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const guests = params.get("guests") || "";
  const date = params.get("date") || "";
  const searching = !!(guests || date);
  const filters: Filters = { type: params.get("type") || "", cap: Number(params.get("cap")) || 0, max: Number(params.get("max")) || 0, sort: params.get("sort") || "" };
  const setFilters = (f: Filters) => {
    const next = new URLSearchParams(params);
    (Object.keys(DEFAULT_FILTERS) as (keyof Filters)[]).forEach((k) => (f[k] ? next.set(k, String(f[k])) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const { data = [], isLoading } = useQuery<Yacht[]>({
    queryKey: ["yachts", guests, date],
    queryFn: async () => (await api.get("/yachts", { params: { guests: guests || undefined, date: date || undefined } })).data,
  });
  const available = data.filter((y) => y.available !== false);
  const list = applyFilters(available, filters);
  const q = searching ? `?guests=${guests}&date=${date}` : "";

  return (
    <div data-testid="yachts-page">
      <section className="bg-[#0B192C] relative grain">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
          <p className="eyebrow !text-[#D4AF37]">{t("yachts.eyebrow")}</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white">{t("yachts.title")}</h1>
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 -mt-12 relative z-10">
        <SearchBar key={`${guests}-${date}`} initialGuests={Number(guests) || 2} initialDate={date} compact />
      </div>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        {!isLoading && available.length > 0 && <FleetFilters all={available} value={filters} onChange={setFilters} />}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <p data-testid="yachts-results-count" className="text-sm text-slate-600">
            {searching ? t("yachts.results", { n: list.length }) : t("yachts.all", { n: list.length })}
            {date && <span className="text-slate-400"> · {fmtDate(date, lang)}</span>}
            {guests && <span className="text-slate-400"> · {guests} {t("card.guests")}</span>}
          </p>
          {searching && <Link to="/yachts" data-testid="yachts-clear-search" className="text-sm font-semibold text-[#008C86] hover:underline">{t("yachts.clear")}</Link>}
        </div>
        {isLoading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#00A8A0]" /></div>
        ) : list.length === 0 ? (
          <p data-testid="yachts-empty" className="mt-16 text-center font-display text-2xl text-slate-500 max-w-xl mx-auto">{available.length > 0 ? t("flt.none") : t("yachts.none")}</p>
        ) : (
          <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {list.map((y) => <YachtCard key={y.id} yacht={y} query={q} />)}
          </div>
        )}
      </section>
    </div>
  );
}
